import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ColumnDef,
} from "material-react-table";
import { Box, Typography } from "@mui/material";

import { MRT_RowData } from "material-react-table";
import "./CustomTable.scss";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import { PRIMARY_GREEN } from "../../utility/constants/common-constants";

interface CustomTableProps<T extends MRT_RowData> {
  data: T[];
  columns: MRT_ColumnDef<T>[];
  title: string;
  renderTopToolbarCustomActions?: () => React.ReactNode;
  enableRowSelection?: boolean;
  totalRecords?: number;
  globalFilterPlaceholder?: string;
  renderEmptyRowsFallback?: () => React.ReactNode;
}

const CustomTable = <T extends MRT_RowData>({
  data,
  columns,
  title,
  renderTopToolbarCustomActions,
  enableRowSelection,
  totalRecords,
  globalFilterPlaceholder,
  renderEmptyRowsFallback,
}: CustomTableProps<T>) => {
  const table = useMaterialReactTable({
    data,
    columns,
    layoutMode: "grid",
    renderTopToolbarCustomActions,
    enablePagination: Boolean(totalRecords) || false,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    enableFilterMatchHighlighting: false,
    enableColumnFilters: true,
    enableSorting: true,
    enableColumnActions: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableDensityToggle: false,
    positionGlobalFilter: "left",
    enableRowSelection,
    rowCount: totalRecords,
    renderEmptyRowsFallback:
      renderEmptyRowsFallback ||
      (() => (
        <div className="no-result">
          <WarningOutlinedIcon />
          <div> No Results Found</div>
        </div>
      )),
    muiTablePaperProps: {
      sx: {
        borderRadius: "4px",
        border: "none",
        boxShadow: "none",
        padding: "2rem 0",
        //stripe the rows, make odd rows a darker color
        "& tr:nth-of-type(even) > td": {
          backgroundColor: "#f2f2f2",
        },
        "& .MuiTableRow-head": {
          backgroundColor: "#F2EEE8",
          marginTop: "40px",
        },

        ".MuiTableContainer-root": {
          overflowX: "scroll",
          "&::-webkit-scrollbar": {
            height: "12px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D9D9D9",
            borderRadius: "10px",
            border: "3px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: PRIMARY_GREEN,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#fff",
            borderRadius: "10px",
          },
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: globalFilterPlaceholder,
      InputProps: {
        sx: {
          minWidth: "500px",

          "&.MuiInputBase-root": {
            borderRadius: "50px",
            position: "relative",
            left: "-8px",
          },
          "& .MuiSvgIcon-root": {
            color: "#BCA685",
          },
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: "1px solid #BCA685",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: "1px solid #BCA685",
        wordBreak: "break-word",
      },
    },
    muiToolbarAlertBannerProps: {
      sx: {
        display: "none",
      },
    },
    muiFilterTextFieldProps: {
      sx: {
        input: {
          fontSize: "14px",
        },
        "&:hover:not(.Mui-disabled, .Mui-error):before": {
          borderBottom: "none",
        },
        "&:before": {
          borderBottom: "none",
        },
        "&:after": {
          borderBottom: "none",
        },
        border: "1px solid #bdbdbd",
        borderRadius: "4px",
        backgroundColor: " #ffffff",
        color: "#000",
        paddingLeft: "8px",
      },
    },
    initialState: {
      showGlobalFilter: true,
      showColumnFilters: true,
      pagination: {
        pageSize: 25,
        pageIndex: 0,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 25, 50, 100],
      showFirstButton: false,
      showLastButton: false,
    },
    sortingFns: {
      customSortFn: (rowA, rowB, columnId) =>
        (rowA.getValue(columnId) as string)
          .toUpperCase()
          .localeCompare((rowB.getValue(columnId) as string).toUpperCase()),
    },
  });

  return (
    <Box>
      <Typography variant="h2">
        {title} ({data.length})
      </Typography>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default CustomTable;
